import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import type {
  DiscoverCollection,
  DiscoverCollectionsCollection,
} from '@freelancer/datastore/collections';
import { ModalService } from '@freelancer/ui';
import { LinkUnderline } from '@freelancer/ui/link';
import { ModalSize } from '@freelancer/ui/modal';
import { QueryParamsHandling } from '@freelancer/ui/tabs';
import { toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DiscoverCollectionTypeApi } from 'api-typings/users/users';
import type { Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserListSaveModalResponse } from '../../../app/user-lists/user-lists-common/user-list-save/user-list-save-modal.types';

@UntilDestroy({ className: 'UserListCreateButtonComponent' })
@Component({
  selector: 'fl-user-list-create-button',
  template: `
    <fl-link
      flTrackingLabel="UserListCreateButtonClick"
      i18n="New list button"
      (click)="handleCreate()"
      [underline]="LinkUnderline.NEVER"
    >
      + New List
    </fl-link>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: [],
})
export class UserListCreateButtonComponent implements OnInit {
  LinkUnderline = LinkUnderline;

  /** Redirect the user to a list's page when created */
  @Input() redirectOnCreate = false;

  @Output() save = new EventEmitter<UserListSaveModalResponse>();

  customLists$: Observable<readonly DiscoverCollection[]>;
  userId$: Observable<number>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private modalService: ModalService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.userId$ = this.auth.getUserId().pipe(map(userId => toNumber(userId)));
    const customCollectionsCollection =
      this.datastore.collection<DiscoverCollectionsCollection>(
        'discoverCollections',
        query =>
          query
            .where('ownerId', '==', this.userId$)
            .where('type', '==', DiscoverCollectionTypeApi.LISTS_CUSTOM),
      );
    this.customLists$ = customCollectionsCollection.valueChanges();
  }

  handleCreate(): void {
    const modalResponse = this.modalService
      .open('UserListSaveModal', {
        edgeToEdge: true,
        size: ModalSize.SMALL,
        mobileFullscreen: true,
      })
      .afterClosed();

    modalResponse.then(res => {
      if (res === UserListSaveModalResponse.CREATE_SUCCESS) {
        this.save.emit(res);

        if (this.redirectOnCreate) {
          // Redirect to the newly created list based on the most recently created discover collection
          const createdCollection$ = this.customLists$.pipe(
            map(lists =>
              lists.reduce((previousList, currentList) =>
                currentList.dateCreated > previousList.dateCreated
                  ? currentList
                  : previousList,
              ),
            ),
          );

          firstValueFrom(createdCollection$.pipe(untilDestroyed(this))).then(
            list => {
              this.router.navigate([`/lists/${list.seoUrl}`], {
                queryParamsHandling: QueryParamsHandling.PRESERVE,
              });
            },
          );
        }
      }
    });
  }
}
